import { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { useQueryClient } from "react-query";
import {
  Box,
  Button,
  Grid,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import AppsIcon from "@mui/icons-material/Apps";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import SearchIcon from "@mui/icons-material/Search";
import CompareArrowsIcon from "@mui/icons-material/CompareArrows";
import { alpha, type Theme } from "@mui/material/styles";
import { PageTopHelpButton } from "src/components/help";
import AllChats from "src/views/assistant/components/splashScreen/AllChats";
import { useChatStore } from "src/views/assistant/stores/ChatStore";
import ChatModeButton from "./components/ChatModeButton";
import { renderChatKindBadge } from "./components/ChatKindChip";
import {
  buildPartsAIChatUrl,
  PARTS_AI_LISTING_PRODUCT_KEY_BY_KIND,
  type PartsAIListingKind,
} from "./utils/chatUrl";

// Selected-state styling for a kind filter button, tinted to match that
// kind's icon/chip color (see ChatKindChip) instead of the group's default.
const selectedFilterSx = (color: "primary" | "info" | "secondary") => ({
  "&.Mui-selected": {
    color: `${color}.main`,
    borderColor: `${color}.main`,
    backgroundColor: (theme: Theme) => alpha(theme.palette[color].main, 0.12),
    "&:hover": {
      backgroundColor: (theme: Theme) => alpha(theme.palette[color].main, 0.2),
    },
  },
});

const PartsAIHomePage = () => {
  const history = useHistory();
  const queryClient = useQueryClient();
  const { setProductConfig, registerChatUrlBuilder } = useChatStore();
  const [kindFilter, setKindFilter] = useState<PartsAIListingKind>("all");
  const productKey = PARTS_AI_LISTING_PRODUCT_KEY_BY_KIND[kindFilter];

  useEffect(() => {
    setProductConfig({
      productKey,
      productName: "Parts AI",
      basePath: "/parts",
    });
    registerChatUrlBuilder(productKey, (dialogueId, rowKind) =>
      buildPartsAIChatUrl(dialogueId, rowKind, queryClient),
    );
  }, [setProductConfig, registerChatUrlBuilder, queryClient, productKey]);

  const handleKindFilterChange = (
    _event: React.MouseEvent<HTMLElement>,
    value: PartsAIListingKind | null,
  ) => {
    if (value !== null) {
      setKindFilter(value);
    }
  };

  return (
    <Box
      sx={{
        flex: 1,
        minHeight: 0,
        width: "100%",
        maxWidth: 1400,
        mx: "auto",
        p: 3,
      }}
    >
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ mb: 3, flexWrap: "wrap", rowGap: 1.5, columnGap: 2 }}
      >
        <Typography variant="h5" fontWeight={600}>
          Parts AI
        </Typography>
        <Stack direction="row" spacing={1}>
          <PageTopHelpButton productLabel="Parts AI" product="parts-ai" />
          <Button
            variant="contained"
            startIcon={<AppsIcon />}
            onClick={() => history.push("/parts/catalog")}
            data-testid="parts-home-catalog-btn"
          >
            Catalog
          </Button>
        </Stack>
      </Stack>

      <Grid container spacing={2} sx={{ mb: 4 }}>
        <Grid item xs={12} md={4}>
          <ChatModeButton
            icon={<Inventory2OutlinedIcon sx={{ fontSize: 40 }} />}
            title="New Part"
            subtitle="Start a new part analysis chat"
            onClick={() => history.push("/parts/chat")}
            testId="parts-home-new-part-btn"
            iconColor="primary.main"
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <ChatModeButton
            icon={<SearchIcon sx={{ fontSize: 40 }} />}
            title="New Search"
            subtitle="Search parts using natural language"
            onClick={() => history.push("/parts/search")}
            testId="parts-home-new-search-btn"
            iconColor="info.main"
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <ChatModeButton
            icon={<CompareArrowsIcon sx={{ fontSize: 40 }} />}
            title="New Comparison"
            subtitle="Compare two parts side by side"
            onClick={() => history.push("/parts/compare")}
            testId="parts-home-new-comparison-btn"
            iconColor="secondary.main"
          />
        </Grid>
      </Grid>

      <AllChats
        productKey={productKey}
        basePath="/parts"
        canStartNewChat={false}
        hideHeader
        rowSpacing={0}
        extraFilters={kindFilter === "all" ? undefined : { kind: kindFilter }}
        renderChatBadge={renderChatKindBadge}
        headerExtra={
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            flexWrap="wrap"
            rowGap={1}
          >
            <Typography variant="h6">Recent Chats</Typography>
            <ToggleButtonGroup
              size="small"
              value={kindFilter}
              exclusive
              onChange={handleKindFilterChange}
              aria-label="Filter recent chats by type"
            >
              <ToggleButton value="all" data-testid="parts-home-filter-all">
                All
              </ToggleButton>
              <ToggleButton
                value="run"
                data-testid="parts-home-filter-part"
                sx={selectedFilterSx("primary")}
              >
                Part
              </ToggleButton>
              <ToggleButton
                value="search"
                data-testid="parts-home-filter-search"
                sx={selectedFilterSx("info")}
              >
                Search
              </ToggleButton>
              <ToggleButton
                value="comparison"
                data-testid="parts-home-filter-comparison"
                sx={selectedFilterSx("secondary")}
              >
                Comparison
              </ToggleButton>
            </ToggleButtonGroup>
          </Stack>
        }
      />
    </Box>
  );
};

export default PartsAIHomePage;
